import { CheckCircleIcon, GearIcon } from "@primer/octicons-react";
import { Box, Spinner } from "@primer/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { setOnDangCapNhat } from "../../api/apiClient";
import { appConst } from "../../AppConst";
import Button from "../button";

/** Giây giữa 2 lần tự kiểm tra lại. */
const CHU_KY = 10;

/**
 * Hệ thống còn trả lời không: gọi thử một đường dẫn API — ứng dụng chạy thì trả dưới 500 (kể cả
 * 404/401); đang dừng thì nginx trả 502/503 (trang cập nhật). Lỗi mạng cũng coi như chưa sẵn sàng.
 */
const heThongChay = async (): Promise<boolean> => {
  try {
    const r = await fetch(`${appConst.baseApiURL}/`, {
      cache: "no-store",
      credentials: "include",
    });
    return r.status < 500;
  } catch {
    return false;
  }
};

type TrangThai = "an" | "dang_cap_nhat" | "xong";

/**
 * Màn phủ "Hệ thống đang cập nhật" cho người ĐANG MỞ phần mềm lúc ứng dụng bị dừng để cập nhật
 * (user 30/09/2026). Trước đây mỗi thao tác đều nổ một thông báo "Có lỗi" khó hiểu.
 *
 * - Lớp gọi API báo 502/503 → KIỂM TRA LẠI một lần; hệ thống vẫn trả lời (lỗi thoáng qua) thì
 *   thôi, không hiện gì.
 * - Đang dừng thật → hiện màn chờ, cứ {@link CHU_KY} giây tự kiểm tra lại.
 * - Chạy lại → mời tải lại trang để dùng bản mới. KHÔNG tự tải: người dùng có thể đang nhập dở.
 *
 * Người mở trang MỚI lúc đang dừng thì do nginx trả trang tĩnh (deploy/nginx/cap-nhat.html).
 */
const ManHinhDangCapNhat = () => {
  const [trangThai, setTrangThai] = useState<TrangThai>("an");
  const [conLai, setConLai] = useState(CHU_KY);
  const [dangKiem, setDangKiem] = useState(false);
  const trangThaiRef = useRef<TrangThai>("an");
  const dangXacNhanRef = useRef(false);

  const datTrangThai = (t: TrangThai) => {
    trangThaiRef.current = t;
    setTrangThai(t);
  };

  // Lớp gọi API báo 502/503.
  useEffect(() => {
    setOnDangCapNhat(() => {
      if (trangThaiRef.current !== "an" || dangXacNhanRef.current) return;
      dangXacNhanRef.current = true;
      heThongChay()
        .then((chay) => {
          if (!chay) {
            setConLai(CHU_KY);
            datTrangThai("dang_cap_nhat");
          }
        })
        .finally(() => {
          dangXacNhanRef.current = false;
        });
    });
    return () => setOnDangCapNhat(null);
  }, []);

  const kiemTra = useCallback(async () => {
    setDangKiem(true);
    const chay = await heThongChay();
    setDangKiem(false);
    if (chay) datTrangThai("xong");
    else setConLai(CHU_KY);
  }, []);

  // Đếm ngược rồi tự kiểm tra lại.
  useEffect(() => {
    if (trangThai !== "dang_cap_nhat" || dangKiem) return;
    const t = setTimeout(() => {
      if (conLai <= 1) kiemTra();
      else setConLai(conLai - 1);
    }, 1000);
    return () => clearTimeout(t);
  }, [trangThai, conLai, dangKiem, kiemTra]);

  if (trangThai === "an") return null;
  const xong = trangThai === "xong";

  return (
    <Box
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="man-cap-nhat-tieu-de"
      sx={{
        position: "fixed",
        inset: 0,
        // Trên mọi hộp thoại / khung nổi đang mở.
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        bg: "rgba(15, 23, 42, 0.55)",
        backdropFilter: "blur(3px)",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 480,
          bg: "canvas.default",
          border: "1px solid",
          borderColor: "border.default",
          borderRadius: 3,
          boxShadow: "shadow.extraLarge",
          px: [3, 4],
          py: [4, 5],
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            color: xong ? "success.fg" : "accent.fg",
            mb: 3,
            display: "flex",
            justifyContent: "center",
            ...(xong
              ? {}
              : {
                  "& svg": { animation: "quayCapNhat 4s linear infinite" },
                  "@keyframes quayCapNhat": { to: { transform: "rotate(360deg)" } },
                  "@media (prefers-reduced-motion: reduce)": {
                    "& svg": { animation: "none" },
                  },
                }),
          }}
        >
          {xong ? <CheckCircleIcon size={56} /> : <GearIcon size={56} />}
        </Box>
        <Box
          as="h2"
          id="man-cap-nhat-tieu-de"
          sx={{ fontSize: [3, 4], fontWeight: 600, m: 0, mb: 2 }}
        >
          {xong ? "Hệ thống đã cập nhật xong" : "Hệ thống đang cập nhật"}
        </Box>
        <Box as="p" sx={{ color: "fg.muted", m: 0, mb: 4, lineHeight: 1.6 }}>
          {xong
            ? "Tải lại trang để dùng phiên bản mới. Nếu đang nhập dở, bạn có thể tiếp tục rồi tải lại sau."
            : "Phần mềm đang được nâng cấp. Thao tác vừa rồi chưa được thực hiện — vui lòng chờ trong giây lát rồi làm lại."}
        </Box>

        {xong ? (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexDirection: ["column", "row"],
            }}
          >
            <Button
              text="Tải lại trang"
              variant="primary"
              onClick={() => window.location.reload()}
            />
            <Button
              text="Tiếp tục làm việc"
              onClick={() => datTrangThai("an")}
            />
          </Box>
        ) : (
          <>
            <Box
              aria-live="polite"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 2,
                px: 3,
                py: 2,
                mb: 3,
                borderRadius: 999,
                bg: "accent.subtle",
                color: "fg.muted",
                fontSize: 1,
              }}
            >
              <Spinner size="small" />
              {dangKiem
                ? "Đang kiểm tra lại…"
                : `Tự kiểm tra lại sau ${conLai} giây`}
            </Box>
            <Box>
              <Button
                text="Thử lại ngay"
                variant="primary"
                disabled={dangKiem}
                onClick={kiemTra}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ManHinhDangCapNhat;
